import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileClock, ListFilter, Plus, Trash2 } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem, IconButton } from "@doculegis/shared";
import {
  applyDraftMatterFilters,
  DraftMatterFilterBar,
  EMPTY_DRAFT_MATTER_FILTERS,
  hasActiveDraftMatterFilters,
  type DraftMatterFilters,
} from "@/apps/drafting/components/DraftMatterFilterBar";
import { GlassCard } from "@/components/common/GlassCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { MobileFab } from "@/components/common/MobileFab";
import { Button } from "@/components/ui/button";
import { useDraftMatters } from "@/hooks/useDraftMatters";
import { usePermission } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import {
  pageActionButtonClassName,
  pageActionDesktopOnlyClassName,
  pageActionLabelClassName,
} from "@/lib/page-action-button";
import { draftMatterStatusLabel } from "@/lib/draft-matter-status";
import type { DraftMatter } from "@/types/drafting";

const PAGE_SIZE = 12;

function DraftCard({
  matter,
  onDelete,
  canDelete,
}: {
  matter: DraftMatter;
  onDelete?: (matter: DraftMatter) => void;
  canDelete: boolean;
}) {
  const href =
    matter.status === "setup"
      ? `/drafting/new?matterId=${matter.id}`
      : matter.status === "background"
        ? `/drafting/new?matterId=${matter.id}&step=2`
        : `/drafting/${matter.id}`;

  return (
    <div className="group relative min-w-0">
      {canDelete && onDelete && matter.is_owner ? (
        <div className="absolute right-3 top-3 z-10">
          <ActionOverflowMenu onClick={(e) => e.stopPropagation()}>
            <ActionOverflowMenuItem
              icon={Trash2}
              label="Delete draft"
              variant="destructive"
              onClick={() => onDelete(matter)}
            />
          </ActionOverflowMenu>
        </div>
      ) : null}
      <Link
        to={href}
        className="block min-w-0 rounded-xl border border-[var(--lm-border-card)] bg-[var(--surface-sunken)] p-4 transition-all hover:border-[rgba(147,51,245,0.4)] dark:rounded-2xl dark:border-indigo-300/20 dark:bg-slate-900/45 dark:hover:border-violet-300/30"
      >
        <p className="mb-2 truncate pr-10 text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-100">
          {matter.title || matter.matter_number}
        </p>
        <p className="truncate text-xs text-[var(--lm-muted)] dark:text-slate-400">
          {matter.matter_number}
        </p>
        {matter.contract_type ? (
          <p className="mt-0.5 truncate text-xs text-[var(--lm-muted)] dark:text-slate-400">
            {matter.contract_type}
          </p>
        ) : null}
        {matter.jurisdiction ? (
          <p className="mt-0.5 truncate text-xs text-[var(--lm-muted)] dark:text-slate-400">
            {matter.jurisdiction}
          </p>
        ) : null}
        <p className="mt-1 text-xs text-[var(--lm-muted)] dark:text-slate-400">
          {new Date(matter.created_at).toLocaleString()}
        </p>
        <span className="mt-2.5 inline-flex rounded-full bg-[var(--tint-purple-bg)] px-2.5 py-0.5 text-[11px] font-semibold capitalize text-[var(--tint-purple-fg)] dark:bg-purple-500/20 dark:text-purple-300">
          {draftMatterStatusLabel(matter.status)}
        </span>
      </Link>
    </div>
  );
}

function DraftMatterPagination({
  page,
  pageCount,
  totalItems,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) {
  if (totalItems <= PAGE_SIZE) return null;

  return (
    <div className="mt-5 flex items-center justify-center gap-3 text-[13px] text-[var(--lm-muted)] dark:mt-4 dark:border-t dark:border-indigo-300/15 dark:pt-4 dark:text-slate-400">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="px-2.5"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <span className="text-[13px] text-[var(--lm-muted)] dark:text-slate-300">
        Page {page} of {pageCount} · {totalItems} draft{totalItems === 1 ? "" : "s"}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="px-2.5"
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function DraftingHome() {
  const { matters, isLoading, error, removeMatter } = useDraftMatters();
  const canCreate = usePermission("draft", "create");
  const canDelete = usePermission("draft", "delete");
  const [filters, setFilters] = useState<DraftMatterFilters>(EMPTY_DRAFT_MATTER_FILTERS);
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DraftMatter | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const sortedMatters = useMemo(
    () =>
      [...matters].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [matters]
  );

  const visibleMatters = useMemo(
    () => applyDraftMatterFilters(sortedMatters, filters),
    [sortedMatters, filters]
  );

  const pageCount = Math.max(1, Math.ceil(visibleMatters.length / PAGE_SIZE));
  const pageItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visibleMatters.slice(start, start + PAGE_SIZE);
  }, [visibleMatters, page]);

  const contractTypeOptions = useMemo(() => {
    const set = new Set<string>();
    matters.forEach((matter) => {
      if (matter.contract_type) set.add(matter.contract_type);
    });
    return Array.from(set).sort();
  }, [matters]);

  const jurisdictionOptions = useMemo(() => {
    const set = new Set<string>();
    matters.forEach((matter) => {
      if (matter.jurisdiction) set.add(matter.jurisdiction);
    });
    return Array.from(set).sort();
  }, [matters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const accessDenied = error?.toLowerCase().includes("insufficient permissions");
  const emptyBecauseFilters =
    !isLoading && !error && visibleMatters.length === 0 && matters.length > 0;

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await removeMatter(deleteTarget.id);
      setDeleteTarget(null);
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsDeleting(false);
    }
  }

  useEffect(() => {
    if (!deleteError) return;
    const timer = window.setTimeout(() => setDeleteError(null), 4000);
    return () => window.clearTimeout(timer);
  }, [deleteError]);

  return (
    <section className="relative px-4 pt-4 md:px-8 md:pt-8">
      <div className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(circle_at_18%_20%,rgba(123,97,255,0.18),transparent_36%),radial-gradient(circle_at_84%_12%,rgba(63,146,255,0.15),transparent_34%)] dark:block" />
      <div className="relative">
        <div className="mb-5 flex flex-row items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-[var(--lm-foreground-strong)] dark:text-white">
              Draft Matters
            </h1>
            <p className="hidden text-sm text-[var(--lm-muted)] sm:block dark:text-slate-300/85">
              Create and manage AI-assisted legal drafts
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!isLoading && matters.length > 0 ? (
              <div className="relative sm:hidden">
                <IconButton
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  title="Filters"
                  aria-label="Filters"
                  className="rounded-lg border-transparent bg-[var(--nav-active-bg)] text-[#7c3aed] hover:border-transparent hover:bg-[rgba(147,51,245,0.15)] hover:text-[#6d28d9] dark:bg-indigo-500/20 dark:text-violet-300 dark:hover:bg-indigo-500/30 dark:hover:text-violet-200"
                >
                  <ListFilter strokeWidth={1.75} />
                </IconButton>
                {hasActiveDraftMatterFilters(filters) ? (
                  <span
                    className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#9333F5] dark:border-slate-900"
                    aria-hidden
                  />
                ) : null}
              </div>
            ) : null}
            {canCreate ? (
              <Button
                variant="accent"
                asChild
                title="New draft"
                className={cn(pageActionButtonClassName, pageActionDesktopOnlyClassName)}
              >
                <Link to="/drafting/new">
                  <Plus className="h-4 w-4" />
                  <span className={pageActionLabelClassName}>New draft</span>
                </Link>
              </Button>
            ) : null}
          </div>
        </div>

        {!isLoading && matters.length > 0 ? (
          <DraftMatterFilterBar
            filters={filters}
            onChange={setFilters}
            contractTypeOptions={contractTypeOptions}
            jurisdictionOptions={jurisdictionOptions}
            mobileSheetOpen={mobileFiltersOpen}
            onMobileSheetOpenChange={setMobileFiltersOpen}
          />
        ) : null}

        <GlassCard className="mb-2 border-[var(--lm-border-card)] bg-white px-4 pb-24 pt-4 shadow-[var(--shadow-card)] dark:border-glass-border dark:bg-slate-950/45 dark:shadow-none sm:pb-10 md:px-6 md:pb-12 md:pt-6">
          {isLoading ? <p className="text-sm text-slate-300/85">Loading drafts...</p> : null}
          {accessDenied ? (
            <div className="rounded-xl border border-indigo-300/20 bg-slate-900/45 p-6 text-center">
              <FileClock className="mx-auto mb-3 h-10 w-10 text-violet-200" />
              <h2 className="text-base font-semibold text-slate-100">Access denied</h2>
              <p className="mt-1 text-sm text-slate-300/85">
                You don&apos;t have permission to view draft matters.
              </p>
            </div>
          ) : null}
          {error && !accessDenied ? <p className="text-sm text-red-300">{error}</p> : null}
          {deleteError ? <p className="mb-3 text-sm text-red-300">{deleteError}</p> : null}

          {!isLoading && !error && visibleMatters.length === 0 ? (
            <div className="rounded-xl border border-indigo-300/20 bg-slate-900/45 p-6 text-center">
              <FileClock className="mx-auto mb-3 h-10 w-10 text-violet-200" />
              <h2 className="text-base font-semibold text-slate-100">
                {emptyBecauseFilters ? "No drafts match your filters" : "No drafts yet"}
              </h2>
              <p className="mt-1 text-sm text-slate-300/85">
                {emptyBecauseFilters
                  ? "Try adjusting or clearing your filters."
                  : "Start a new draft matter to begin AI-assisted document drafting."}
              </p>
              {emptyBecauseFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-3"
                  onClick={() => setFilters(EMPTY_DRAFT_MATTER_FILTERS)}
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          ) : null}

          {pageItems.length > 0 ? (
            <>
              <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
                {pageItems.map((matter) => (
                  <DraftCard
                    key={matter.id}
                    matter={matter}
                    onDelete={setDeleteTarget}
                    canDelete={canDelete}
                  />
                ))}
              </div>
              <DraftMatterPagination
                page={page}
                pageCount={pageCount}
                totalItems={visibleMatters.length}
                onPageChange={setPage}
              />
            </>
          ) : null}
        </GlassCard>
      </div>

      {canCreate ? <MobileFab to="/drafting/new" label="New draft" /> : null}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete draft?"
        description={
          deleteTarget
            ? `Permanently delete "${deleteTarget.title || deleteTarget.matter_number}"?`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={isDeleting}
      />
    </section>
  );
}
